// @ts-nocheck
import { formatMYR } from "@kawkaw/shared";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { enUS as dfEn, zhCN as dfZh } from "date-fns/locale";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BellOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ContainerOutlined,
  DollarOutlined,
  EllipsisOutlined,
  FireOutlined,
  ShoppingOutlined,
} from "../components/admin-icons";
import { Dropdown, Spin } from "../components/admin-ui";
import { StatusBadge as StandardStatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import {
  useAdminDailyReport,
  useAdminOrders,
  useAdminTopItems,
  useDashboardStats,
  useRevenueReport,
} from "../api/hooks";
import { getOrderCustomerDisplay } from "../utils/orderUi";

type RecentOrder = {
  id: string;
  order_code: string | null;
  type: string;
  status: string;
  total: string;
  created_at: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  special_note?: string | null;
  customer?: { name?: string | null; phone?: string | null } | null;
};

function formatLocalDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function money(value: string | number | undefined | null) {
  const n = Number(value ?? 0);
  return n.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatRmAxis(value: number) {
  if (value >= 1000) return `RM ${(value / 1000).toFixed(2)}k`;
  return formatMYR(value);
}

function KpiCard({
  label,
  value,
  icon,
  accent = "#f59e0b",
  topAccent = false,
  sub,
  progress,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  accent?: string;
  topAccent?: boolean;
  sub?: React.ReactNode;
  progress?: number;
}) {
  return (
    <section
      className={`admin-kpi-card flex min-h-[188px] flex-col rounded-xl bg-white px-6 py-6 shadow-[0_12px_16px_rgba(25,28,29,0.04)] ${
        topAccent ? "admin-kpi-card-revenue" : ""
      }`}
      style={{ borderTopColor: topAccent ? accent : undefined }}
    >
      <div className="admin-kpi-heading flex items-start justify-between gap-4">
        <div className="admin-kpi-label max-w-[145px] text-[12px] font-extrabold uppercase leading-4 tracking-[1.2px] text-[#534434]">
          {label}
        </div>
        <div className="admin-kpi-icon text-[20px]" style={{ color: accent }}>
          {icon}
        </div>
      </div>

      {/* flex-1 + mt-auto on sub below keeps progress/sub clear of a tall (e.g. two-line) value */}
      <div className="admin-kpi-value mt-5 flex-1 text-[30px] font-black leading-9 text-[#191c1d] tabular-nums">
        {value}
      </div>

      {progress != null && (
        <div className="admin-kpi-progress mt-4 h-1.5 overflow-hidden rounded-full bg-[#e7e8e9]">
          <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: accent }} />
        </div>
      )}

      {sub && <div className="admin-kpi-sub mt-auto pt-3 text-[10px] font-extrabold uppercase leading-[15px]">{sub}</div>}
    </section>
  );
}

function StatusTile({
  label,
  count,
  icon,
  color,
  iconBg,
}: {
  label: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  iconBg: string;
}) {
  return (
    <div className="admin-status-tile flex h-full min-h-[170px] flex-1 items-center justify-between rounded-xl bg-white p-6">
      <div className="admin-status-content">
        <div className="admin-status-label whitespace-pre-line text-[12px] font-extrabold uppercase leading-4 tracking-[1.2px] text-[#534434]">
          {label}
        </div>
        <div className="admin-status-count mt-3 text-[36px] font-black leading-10 tabular-nums" style={{ color }}>
          {String(count).padStart(2, "0")}
        </div>
      </div>
      <div className="admin-status-icon grid h-12 w-12 shrink-0 place-items-center rounded-full text-[20px] leading-none [&>svg]:block" style={{ background: iconBg, color }}>
        {icon}
      </div>
    </div>
  );
}

function TopItem({
  item,
  fallback,
  period,
}: {
  item: { name: string; qty: number; revenue: string; imageUrl?: string | null };
  fallback: number;
  period: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="admin-top-item grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3">
      {item.imageUrl ? (
        <img src={item.imageUrl} alt={item.name} className="admin-top-item-image h-11 w-11 rounded-[10px] object-cover" />
      ) : (
        <div className="admin-top-item-fallback flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#191c1d] text-sm font-black text-white">
          {fallback}
        </div>
      )}
      <div className="min-w-0">
        <div className="admin-top-item-name truncate text-[13px] font-black leading-4 text-[#191c1d]">{item.name}</div>
        <div className="admin-top-item-quantity mt-1 text-[10px] font-extrabold uppercase leading-3 text-[#534434]">
          {t("dashboard.unitsSold", { count: item.qty })}
        </div>
      </div>
      <div className="admin-top-item-sales text-right">
        <div className="admin-top-item-amount text-[12px] font-black text-[#006c49]">RM {money(item.revenue)}</div>
        <div className="admin-top-item-period mt-1 text-[9px] font-bold text-[#534434]">{period}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return <StandardStatusBadge status={status} kind="order" size="xs" className="admin-data-badge" />;
}

export function DashboardScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateFnsLocale = i18n.language?.startsWith("zh") ? dfZh : dfEn;
  const weekdayLocale = i18n.language?.startsWith("zh") ? "zh-CN" : "en-US";
  const [chartView, setChartView] = useState<"today" | "weekly">("today");

  const { data: statsData, isLoading: statsLoading } = useDashboardStats();
  const { data: dailyData, isLoading: dailyLoading } = useAdminDailyReport();
  const { data: topItemsData, isLoading: topItemsLoading } = useAdminTopItems({ limit: 4 });
  const { data: ordersData, isLoading: ordersLoading } = useAdminOrders({ limit: 5 });

  const stats = statsData?.data;
  const daily = dailyData?.data;
  const recentOrders = (ordersData?.data ?? []) as RecentOrder[];

  const pickupOrders = stats?.pickupOrders ?? 0;
  const deliveryOrders = stats?.deliveryOrders ?? 0;
  const totalChannelOrders = pickupOrders + deliveryOrders;
  const pickupPct = totalChannelOrders > 0 ? Math.round((pickupOrders / totalChannelOrders) * 100) : 0;
  const deliveryPct = totalChannelOrders > 0 ? Math.max(0, 100 - pickupPct) : 0;
  const revenueChange = stats?.revChangePercent ?? null;

  const todayStr = formatLocalDate(new Date());
  const weekFromDate = new Date();
  weekFromDate.setDate(weekFromDate.getDate() - 6);
  const weekFromStr = formatLocalDate(weekFromDate);
  const { data: weeklyRaw } = useRevenueReport({ from: weekFromStr, to: todayStr, granularity: "daily" });

  const hourlyBars = (daily?.hourly ?? [])
    .filter((h) => (h.hour >= 8 || Number(h.revenue) > 0) && h.hour <= Math.max(new Date().getHours(), 18))   // every hour of the day so far — never cut (a sale at 11:00 must not fall off the left edge)
    .map((h) => ({ label: `${String(h.hour).padStart(2, "0")}:00`, revenue: Number(h.revenue), current: h.hour === new Date().getHours() }));

  const weeklyBars = (weeklyRaw?.data ?? []).map((d) => {
    const [year, month, day] = d.label.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return {
      label: d.label === todayStr ? t("dashboard.todayLabel", { defaultValue: "TODAY" }) : date.toLocaleDateString(weekdayLocale, { weekday: "short" }).toUpperCase(),
      revenue: Number(d.revenue),
      current: d.label === todayStr,
    };
  });

  const revenueBars = chartView === "today" ? hourlyBars : weeklyBars.slice(-7);
  const topItems = (daily?.topItems?.length ? daily.topItems : topItemsData?.data ?? []).slice(0, 4);
  const topItemsPeriod = daily?.topItems?.length ? t("dashboard.topItemPeriodToday", { defaultValue: "Today" }) : t("dashboard.topItemPeriodAll", { defaultValue: "All time" });

  const donutStyle = useMemo(() => {
    if (!totalChannelOrders) return { background: "#e7e8e9" };
    return { background: `conic-gradient(#006c49 0 ${pickupPct}%, #f59e0b ${pickupPct}% 100%)` };
  }, [pickupPct, totalChannelOrders]);

  const isLoading = statsLoading || dailyLoading;

  return (
    <AdminShell>
      <div className="admin-dashboard-page min-h-full bg-[#f8f9fa] px-8 pb-10 pt-7 font-sans text-[#191c1d]">
        {isLoading ? (
          <div className="flex min-h-[420px] items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
          <div className="mx-auto flex flex-col gap-8">
            <section className="admin-dashboard-kpis grid grid-cols-1 gap-6 xl:grid-cols-4">
              <KpiCard
                label={t("dashboard.totalRevenue")}
                topAccent
                accent="#f59e0b"
                icon={<DollarOutlined />}
                value={
                  <div className="admin-kpi-money">
                    <div className="admin-kpi-currency">RM</div>
                    <div className="admin-kpi-amount">{money(daily?.totalRevenue ?? stats?.todayRevenue)}</div>
                  </div>
                }
                sub={
                  <span className={revenueChange == null || revenueChange >= 0 ? "text-[#006c49]" : "text-[#c62828]"}>
                    {revenueChange == null ? "" : `${revenueChange >= 0 ? "+" : ""}${revenueChange}% ${t("dashboard.fromYesterday")}`}
                  </span>
                }
              />
              <KpiCard
                label={t("dashboard.totalOrdersToday")}
                icon={<ContainerOutlined />}
                accent="#534434"
                value={stats?.todayOrders ?? 0}
              />
              <KpiCard
                label={t("dashboard.pickupOrders")}
                icon={<ShoppingOutlined />}
                accent="#006c49"
                value={pickupOrders}
                progress={pickupPct}
              />
              <KpiCard
                label={t("dashboard.deliveryOrders")}
                icon={<CarOutlined />}
                accent="#00658b"
                value={deliveryOrders}
                progress={deliveryPct}
              />
            </section>

            <section className="admin-dashboard-operations grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
              <div className="admin-orderflow-tiles flex min-h-[289px] gap-2 rounded-2xl bg-[#f3f4f5] p-2 max-lg:flex-col">
                <StatusTile
                  label={t("dashboard.newNow").replace(/\s+/g, "\n")}
                  count={stats?.newNow ?? 0}
                  icon={<BellOutlined />}
                  color="#c62828"
                  iconBg="#ffdad6"
                />
                <StatusTile
                  label={t("dashboard.preparingNow").replace(/\s+/g, "\n")}
                  count={stats?.preparingNow ?? 0}
                  icon={<FireOutlined />}
                  color="#855300"
                  iconBg="#ffddb8"
                />
                <StatusTile
                  label={t("dashboard.readyForPickup").replace(/\s+/g, "\n")}
                  count={stats?.readyForPickup ?? 0}
                  icon={<CheckCircleOutlined />}
                  color="#006c49"
                  iconBg="#6ffbbe"
                />
                <StatusTile
                  label={t("dashboard.readyForDelivery").replace(/\s+/g, "\n")}
                  count={stats?.readyForDelivery ?? 0}
                  icon={<CarOutlined />}
                  color="#1abdff"
                  iconBg="#c5e7ff"
                />
              </div>

              <div className="admin-channel-card flex min-h-[289px] flex-col rounded-xl border border-[rgba(216,195,173,0.1)] bg-white p-6 shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
                <div className="admin-channel-heading text-[12px] font-extrabold uppercase leading-4 tracking-[1.2px] text-[#534434]">
                  {t("dashboard.channelDistribution")}
                </div>
                <div className="admin-channel-body flex flex-1 items-center justify-center py-4">
                  <div className="admin-channel-ring relative h-40 w-40 rounded-full" style={donutStyle}>
                    <div className="admin-channel-center absolute inset-[22px] flex flex-col items-center justify-center rounded-full bg-white">
                      <div className="admin-channel-value text-xl font-black text-[#191c1d]">{totalChannelOrders}</div>
                      <div className="admin-channel-caption text-[8px] font-extrabold uppercase text-[#534434]">{t("dashboard.todayOrdersShort")}</div>
                    </div>
                  </div>
                </div>
                <div className="admin-channel-legend flex justify-center gap-4">
                  <div className="admin-channel-legend-item flex items-center gap-2 text-[10px] font-black uppercase text-[#191c1d]">
                    <span className="h-3 w-3 rounded-full bg-[#f59e0b]" />
                    <span className="admin-channel-label">{t("orders.type.delivery")}</span>
                    <span className="admin-channel-share">{deliveryPct}%</span>
                  </div>
                  <div className="admin-channel-legend-item flex items-center gap-2 text-[10px] font-black uppercase text-[#191c1d]">
                    <span className="h-3 w-3 rounded-full bg-[#006c49]" />
                    <span className="admin-channel-label">{t("orders.type.pickup")}</span>
                    <span className="admin-channel-share">{pickupPct}%</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-dashboard-insights grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
              <div className="admin-dashboard-panel admin-revenue-panel rounded-xl bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
                <div className="admin-dashboard-panel-header flex items-start justify-between gap-4 p-6">
                  <div>
                    <div className="admin-dashboard-panel-title text-xl font-black leading-7 text-[#191c1d]">{t("dashboard.revenueTrend")}</div>
                    <div className="admin-dashboard-panel-subtitle mt-1 text-xs font-medium text-[#534434]">{t("dashboard.revenueTrendSub")}</div>
                  </div>
                  <div className="admin-revenue-tabs flex rounded-lg bg-[#f3f4f5] p-1">
                    {(["today", "weekly"] as const).map((view) => (
                      <button
                        key={view}
                        type="button"
                        onClick={() => setChartView(view)}
                        className={`admin-revenue-tab rounded-md px-3 py-1.5 text-[10px] font-black uppercase ${
                          chartView === view ? "admin-revenue-tab-active bg-white text-[#191c1d] shadow-sm" : "text-[#534434]"
                        }`}
                      >
                        {view === "today" ? t("dashboard.todayTab") : t("dashboard.weeklyTab")}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="admin-revenue-chart h-[220px] px-6 py-2">
                  {revenueBars.length === 0 || revenueBars.every((b) => b.revenue === 0) ? (
                    <div className="admin-dashboard-empty flex h-full w-full items-center justify-center rounded-xl border border-dashed border-[#e7e8e9] text-sm font-bold text-[#737373]">
                      {t("dashboard.noSalesToday")}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenueBars} barCategoryGap="24%" margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke="rgba(231,232,233,0.92)" strokeDasharray="0" vertical={false} />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={({ x, y, payload }) => {
                            const item = revenueBars.find((bar) => bar.label === payload.value);
                            return (
                              <text
                                x={x}
                                y={y + 14}
                                textAnchor="middle"
                                fontSize={9}
                                fontWeight={900}
                                fill={item?.current ? "#ff8c00" : "#534434"}
                              >
                                {payload.value}
                              </text>
                            );
                          }}
                        />
                        <YAxis
                          width={54}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fill: "#737373", fontWeight: 800 }}
                          tickFormatter={(value) => formatRmAxis(Number(value))}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(245,158,11,0.08)", radius: 8 }}
                          formatter={(value) => [`RM ${money(Number(value))}`, t("dashboard.tooltipRevenue")]}
                          contentStyle={{
                            border: "1px solid #e7e8e9",
                            borderRadius: 12,
                            boxShadow: "0 12px 24px rgba(25,28,29,0.08)",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        />
                        <Bar dataKey="revenue" radius={[8, 8, 0, 0]} maxBarSize={78}>
                          {revenueBars.map((bar, index) => (
                            <Cell
                              key={`${bar.label}-${index}`}
                              fill={bar.current ? "#ff8c00" : `rgba(245,158,11,${0.22 + index * 0.1})`}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="admin-dashboard-panel admin-top-items-panel rounded-xl bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]" aria-busy={topItemsLoading}>
                <div className="admin-dashboard-panel-header border-b border-[#f3f4f5] p-6">
                  <div className="admin-dashboard-panel-title text-xl font-black leading-7 text-[#191c1d]">{t("dashboard.topSelling")}</div>
                </div>
                <div className="admin-top-items-list flex flex-col gap-5 p-6">
                  {topItems.length === 0 ? (
                    <div className="admin-dashboard-empty rounded-xl border border-dashed border-[#e7e8e9] p-8 text-center text-sm font-bold text-[#737373]">
                      {t("dashboard.noSalesToday")}
                    </div>
                  ) : (
                    topItems.map((item, index) => (
                      <TopItem key={item.menuItemId ?? item.name} item={item} fallback={index + 1} period={topItemsPeriod} />
                    ))
                  )}
                </div>
              </div>
            </section>

            <section className="admin-dashboard-panel admin-recent-panel admin-data-table-panel overflow-hidden rounded-xl bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
              <div className="admin-dashboard-panel-header flex items-center justify-between gap-4 p-6">
                <div className="admin-dashboard-panel-title text-xl font-black leading-7 text-[#191c1d]">{t("dashboard.recentActivity")}</div>
                <button
                  type="button"
                  onClick={() => navigate("/orders")}
                  className="admin-dashboard-history-link text-xs font-black uppercase text-[#ea580c]"
                >
                  {t("dashboard.viewAllHistory")}
                </button>
              </div>
              <div className="admin-table-wrap overflow-x-auto">
                <table className="admin-table w-full min-w-[860px] border-collapse text-left">
                  <thead className="bg-[#f8f9fa]">
                    <tr className="text-[10px] font-black uppercase tracking-[1.2px] text-[#534434]">
                      <th className="px-6 py-4">{t("dashboard.colOrderId")}</th>
                      <th className="px-6 py-4">{t("dashboard.colChannel")}</th>
                      <th className="px-6 py-4">{t("dashboard.colCustomer")}</th>
                      <th className="px-6 py-4">{t("dashboard.colAmount")}</th>
                      <th className="px-6 py-4">{t("dashboard.colStatus")}</th>
                      <th className="px-6 py-4">{t("dashboard.colTime")}</th>
                      <th className="px-6 py-4 text-right">{t("dashboard.colAction")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersLoading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <Spin />
                        </td>
                      </tr>
                    ) : recentOrders.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-sm font-bold text-[#737373]">
                          <div className="admin-table-empty">{t("dashboard.noRecentOrders", { defaultValue: t("dashboard.noSalesToday") })}</div>
                        </td>
                      </tr>
                    ) : (
                      recentOrders.map((order) => {
                        const customer = getOrderCustomerDisplay(order);
                        return (
                          <tr key={order.id} className="border-t border-[#f3f4f5] text-sm text-[#191c1d]">
                            <td className="admin-data-code px-6 py-5 font-black">#{order.order_code ?? order.id.slice(-8)}</td>
                            <td className="px-6 py-5">
                              <span className="inline-flex items-center gap-2">
                                {order.type === "delivery" ? <CarOutlined className="text-[#00658b]" /> : <ShoppingOutlined className="text-[#006c49]" />}
                                {order.type === "delivery" ? t("orders.type.delivery") : t("orders.type.pickup")}
                              </span>
                            </td>
                            <td className="px-6 py-5">
                              {customer.name || customer.phone ? (
                                <div>
                                  <div className="admin-data-customer font-semibold leading-5 text-[#191c1d]">{customer.name ?? customer.phone}</div>
                                  {customer.phone && customer.name && (
                                    <div className="admin-data-secondary text-xs leading-4 text-[#534434]">{customer.phone}</div>
                                  )}
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="admin-data-amount px-6 py-5 font-black">RM {money(order.total)}</td>
                            <td className="px-6 py-5"><StatusBadge status={order.status} /></td>
                            <td className="admin-data-secondary admin-data-date px-6 py-5 text-xs text-[#534434]">
                              {formatDistanceToNow(new Date(order.created_at), { addSuffix: true, locale: dateFnsLocale })}
                            </td>
                            <td className="px-6 py-5 text-right">
                              <Dropdown
                                trigger={["click"]}
                                menu={{
                                  items: [
                                    {
                                      key: "view",
                                      label: t("dashboard.viewDetails"),
                                      onClick: () => navigate(`/orders/${order.id}`),
                                    },
                                  ],
                                }}
                              >
                                <button
                                  type="button"
                                  className="admin-data-more inline-flex items-center justify-center"
                                  title={t("dashboard.viewDetails")}
                                >
                                  <EllipsisOutlined />
                                </button>
                              </Dropdown>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
